<template>
  <StModal :open="true" title="Mirror Attacker ↔ Defender" @close="$emit('cancel')">
    <p class="modal-sub">
      Replaces the destination zones with mirrored copies of the source zones (positions and labels
      included).
    </p>

    <StField label="Mirror across" class="field">
      <StSegmentedControl v-model="mode" :options="modeOptions" />
    </StField>

    <div class="field">
      <span class="field-label st-eyebrow">Mirror from</span>
      <div class="opt-row">
        <StButton
          :class="['opt-btn', 'from-attacker', { active: from === ZoneType.Attacker }]"
          variant="secondary"
          @click="from = ZoneType.Attacker"
        >
          Attacker ({{ attackerCount }})
        </StButton>
        <StButton
          :class="['opt-btn', 'from-defender', { active: from === ZoneType.Defender }]"
          variant="secondary"
          @click="from = ZoneType.Defender"
        >
          Defender ({{ defenderCount }})
        </StButton>
      </div>
    </div>

    <p class="modal-warn">
      Deletes {{ destCount }} {{ destLabel }} zone{{ destCount === 1 ? '' : 's' }} and creates
      {{ sourceCount }} mirrored {{ destLabel }} zone{{ sourceCount === 1 ? '' : 's' }}.
    </p>

    <template #footer>
      <StButton variant="ghost" @click="$emit('cancel')">Cancel</StButton>
      <StButton variant="primary" :disabled="sourceCount === 0" @click="onApply">Apply</StButton>
    </template>
  </StModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Mirror, ZoneType } from '@scrolldex/shared';
import { MIRROR_OPTIONS } from './labels.js';
import { StModal, StButton, StField, StSegmentedControl } from '../ui/index.js';

const props = defineProps<{ attackerCount: number; defenderCount: number }>();
const emit = defineEmits<{ apply: [payload: { mode: Mirror; from: ZoneType }]; cancel: [] }>();

const mode = ref<Mirror>(Mirror.Both);
// Default to whichever side actually has zones to mirror.
const from = ref<ZoneType>(
  props.attackerCount === 0 && props.defenderCount > 0 ? ZoneType.Defender : ZoneType.Attacker,
);

const modeOptions = MIRROR_OPTIONS.filter((o) => o.val !== 'none').map((o) => ({
  value: o.val as Mirror,
  icon: o.icon,
  label: o.name.replace('Mirror ', ''),
}));

const sourceCount = computed(() =>
  from.value === ZoneType.Attacker ? props.attackerCount : props.defenderCount,
);
const destCount = computed(() =>
  from.value === ZoneType.Attacker ? props.defenderCount : props.attackerCount,
);
const destLabel = computed(() => (from.value === ZoneType.Attacker ? 'Defender' : 'Attacker'));

function onApply() {
  if (sourceCount.value === 0) return;
  emit('apply', { mode: mode.value, from: from.value });
}
</script>

<style scoped>
.modal-sub {
  margin: 0 0 var(--st-space-4);
  font-size: var(--st-text-md);
  color: var(--st-text-muted);
}

.field {
  margin-bottom: var(--st-space-4);
}

.field-label {
  display: block;
  margin-bottom: var(--st-space-2);
}

.opt-row {
  display: flex;
  gap: var(--st-space-2);
  flex-wrap: wrap;
}

/* Attacker/Defender are game-rule identities — keep the literal zone colors so
   the picker matches the public BattleplanMap render. */
.opt-btn.from-attacker.active {
  border-color: #c83c3c;
  background: #fdeaea;
  color: #c83c3c;
}
.opt-btn.from-defender.active {
  border-color: #3c6ec8;
  background: #eaf0fb;
  color: #3c6ec8;
}

.modal-warn {
  margin: 0;
  font-size: var(--st-text-sm);
  color: var(--st-warning-hover);
  background: var(--st-warning-tint);
  border: 1px solid var(--st-warning);
  border-radius: var(--st-radius-sm);
  padding: var(--st-space-2) var(--st-space-3);
}
</style>
